<template>
  <div>
    <the-section :title="$t('ec-line010.form.title')">
      <template #subTitle>
        <span class="text-base lg:text-xs">{{ $t('common.requiredItem') }}</span>
      </template>
      <el-form ref="form" :model="form" label-position="top" :rules="rules">
        <el-form-item
          v-if="corporateFlag && form.business_account_flag"
          :label="$t('common.memberInformation.companyName')"
          prop="company_name"
          class="is-required"
        >
          <el-input
            v-model="form.company_name"
            :placeholder="$t('common.informationPlaceholder.companyName')"
            :maxlength="[form.business_account_flag ? 16 : 255]"
          ></el-input>
        </el-form-item>
        <el-form-item v-if="corporateFlag" :label="$t('common.memberInformation.storeName')" prop="store_name">
          <el-input
            v-model="form.store_name"
            :placeholder="$t('common.informationPlaceholder.storeName')"
            :maxlength="16"
          ></el-input>
        </el-form-item>
        <span v-if="corporateFlag || !form.business_account_flag">
          <el-form-item :label="nameLabel" required>
            <div class="flex space-x-4 items-center">
              <div class="w-1/2">
                <el-form-item prop="name01">
                  <el-input
                    v-model="form.name01"
                    :placeholder="$t('common.informationPlaceholder.lastNamePlaceholder')"
                    :maxlength="[corporateFlag || form.business_account_flag ? 7 : 16]"
                  ></el-input>
                </el-form-item>
              </div>
              <div class="w-1/2">
                <el-form-item prop="name02">
                  <el-input
                    v-model="form.name02"
                    :placeholder="$t('common.informationPlaceholder.firstNamePlaceholder')"
                    :maxlength="[corporateFlag || form.business_account_flag ? 7 : 20]"
                  ></el-input>
                </el-form-item>
              </div>
            </div>
          </el-form-item>

          <el-form-item :label="nameKanaLabel" required>
            <div class="flex space-x-4 items-center">
              <div class="w-1/2">
                <el-form-item prop="kana01">
                  <el-input
                    v-model="form.kana01"
                    :placeholder="$t('common.informationPlaceholder.lastNameKanaPlaceholder')"
                    :maxlength="40"
                  ></el-input>
                </el-form-item>
              </div>
              <div class="w-1/2">
                <el-form-item prop="kana02">
                  <el-input
                    v-model="form.kana02"
                    :placeholder="$t('common.informationPlaceholder.firstNameKanaPlaceholder')"
                    :maxlength="40"
                  ></el-input>
                </el-form-item>
              </div>
            </div>
          </el-form-item>
        </span>
        <span :rules="rules" v-else>
          <el-form-item
            :label="$t('common.memberInformation.companyName')"
            prop="company_name"
            class="is-required"
            required
          >
            <el-input
              v-model="form.company_name"
              :placeholder="$t('common.informationPlaceholder.exampleCompanyName')"
              :maxlength="32"
            ></el-input>
          </el-form-item>
          <el-form-item
            :label="$t('common.memberInformation.companyNameKana')"
            prop="company_name_kana"
            class="is-required"
            required
          >
            <el-input
              v-model="form.company_name_kana"
              :placeholder="$t('common.informationPlaceholder.exampleCompanyNameKana')"
              :maxlength="32"
            ></el-input>
          </el-form-item>
          <div></div>
        </span>

        <el-form-item
          v-if="!corporateFlag && !form.business_account_flag"
          :label="$t('common.memberInformation.birthday')"
          required
        >
          <div class="flex space-x-2 items-center">
            <div class="w-1/3 lg:w-1/2 inline-flex items-center">
              <el-form-item prop="birthday_year">
                <el-select
                  v-model="form.birthday_year"
                  :placeholder="$t('common.informationPlaceholder.yearPlaceholder')"
                >
                  <el-option v-for="year in YEARS" :key="year" :value="year">{{ year }}</el-option>
                </el-select>
              </el-form-item>

              <div class="ml-2 text-center">{{ $t('common.dateSeparator') }}</div>
            </div>
            <div class="w-1/3 lg:w-1/4 inline-flex items-center">
              <el-form-item prop="birthday_month">
                <el-select
                  v-model="form.birthday_month"
                  :placeholder="$t('common.informationPlaceholder.monthPlaceholder')"
                  @change="form.birthday_day = 1"
                >
                  <el-option v-for="month in MONTHS" :key="month" :value="month">{{ month }}</el-option>
                </el-select>
              </el-form-item>
              <div class="ml-2 text-center">{{ $t('common.dateSeparator') }}</div>
            </div>
            <div class="w-1/3 lg:w-1/4">
              <el-form-item prop="birthday_day">
                <el-select
                  v-model="form.birthday_day"
                  :placeholder="$t('common.informationPlaceholder.dayPlaceholder')"
                >
                  <el-option v-for="date in DATES" :key="date" :value="date">{{ date }}</el-option>
                </el-select>
              </el-form-item>
            </div>
          </div>
        </el-form-item>

        <el-form-item
          v-if="!corporateFlag && !form.business_account_flag"
          :label="$t('common.memberInformation.gender')"
          prop="sex"
          required
        >
          <el-radio-group v-model="form.sex">
            <el-radio v-for="item in sex" :key="item.id" class="radio-dot" :label="item.id">{{ item.name }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.email')" prop="email">
          <el-input
            v-model="form.email"
            :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
            :maxlength="255"
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.emailConfirm')" prop="email_confirm">
          <el-input
            v-model="form.email_confirm"
            :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
            :maxlength="255"
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.postalCode')" prop="postal_code" required>
          <div class="flex space-x-4 items-center">
            <div class="w-1/4 inline-flex items-center">
              <el-form-item prop="postal_code01">
                <el-input
                  inputmode="numeric"
                  v-model="form.postal_code01"
                  :placeholder="$t('common.informationPlaceholder.postalCodeFirstPlaceholder')"
                  :maxlength="3"
                  @blur="getAddress"
                  @change="onChangePostalCodeForm"
                ></el-input>
              </el-form-item>
              <div class="ml-2 text-center">{{ $t('common.hyphenSeparator') }}</div>
            </div>
            <div class="w-1/4">
              <el-form-item prop="postal_code02">
                <el-input
                  inputmode="numeric"
                  v-model="form.postal_code02"
                  :placeholder="$t('common.informationPlaceholder.postalCodeLastPlaceholder')"
                  :maxlength="4"
                  @blur="getAddress"
                  @change="onChangePostalCodeForm"
                ></el-input>
              </el-form-item>
            </div>
            <div class="w-1/2 text-gray-950 text-xs font-normal lg:text-lg lg:leading-none">
              {{ $t('common.informationPlaceholder.postalCodeTip') }}
            </div>
          </div>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.prefectures')" prop="pref_id">
          <div class="flex">
            <div class="w-full lg:w-1/2">
              <el-select v-model="form.pref_id" :placeholder="$t('common.pleaseSelect')">
                <el-option v-for="item in prefs" :key="item.id" :value="item.id" :label="item.name"></el-option>
              </el-select>
            </div>
          </div>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr01')" prop="addr01">
          <div class="flex">
            <div class="w-full lg:w-1/2">
              <el-input
                v-model="form.addr01"
                :placeholder="$t('common.informationPlaceholder.municipalitiesPlaceholder')"
                :maxlength="40"
                v-replace-half-width
              ></el-input>
            </div>
          </div>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr02')" prop="addr02">
          <div class="flex">
            <div class="w-full lg:w-1/2">
              <el-input
                v-model="form.addr02"
                :placeholder="$t('common.informationPlaceholder.addressPlaceholder')"
                :maxlength="40"
                v-replace-half-width
              ></el-input>
            </div>
          </div>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.addr03')" prop="addr03">
          <el-input
            v-model="form.addr03"
            :placeholder="$t('common.informationPlaceholder.buildingApartmentNamePlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('common.memberInformation.phoneNumber')" prop="phone_number">
          <el-input
            inputmode="numeric"
            v-model="form.phone_number"
            :placeholder="$t('common.informationPlaceholder.phoneNumberPlaceholder')"
            :maxlength="11"
          ></el-input>
          <p class="text-gray-950 text-xs font-normal pt-1">{{ $t('common.messages.phoneNumberInputRules') }}</p>
        </el-form-item>
      </el-form>
    </the-section>
    <div class="flex flex-col items-center lg:flex-row justify-center mt-5 lg:mt-10 lg:mb-4">
      <el-button class="submit-btn lg:ml-6 lg:order-1" @click="onSubmit">{{ $t('common.action.change') }}</el-button>
      <el-button class="cancel-btn mt-5 mb-1 lg:my-0" @click="onCancel">{{ $t('common.action.cancel') }}</el-button>
    </div>
  </div>
</template>

<script>
import TheSection from '@/components/my-page-partials/TheSection.vue'
import { PostCodeJpService } from '@/services'
import { changeHalfNumber, isValidPostalCode, toHalfWidth, trimCommaForm, trimForm } from '@/utils'

export default {
  components: { TheSection },
  props: {
    value: { type: Object, default: () => {} },
    beforeChangePhoneNumber: { type: String, default: '' },
    corporateFlag: { type: Boolean, default: false },
  },
  data() {
    const sameEmailValidator = (rule, value, callback) => {
      if (this.form.email !== value) {
        callback(new Error(this.$t('common.validate.confirm-email-check')))
      }
      callback()
    }
    const postalCodeValidator = async (rule, value, callback) => {
      const formValue = trimForm(this.form)
      const postalCode01 = formValue.postal_code01
      const postalCode02 = formValue.postal_code02

      if (postalCode01 && postalCode02 && isValidPostalCode({ postalCode01, postalCode02 })) {
        callback()
      } else if (
        (!postalCode01 && !postalCode02) ||
        !postalCode01 ||
        postalCode01.length < 3 ||
        !postalCode02 ||
        postalCode02.length < 4
      ) {
        callback(new Error(this.$t('common.validate.cmn-err-001', [this.$t('common.validate.field.postalCode')])))
      }
    }

    return {
      rules: {
        name01: [
          this.Utils.validate.required(this.validateLastNameLabel()),
          this.Utils.validate.shiftjis(this.$t('common.validate.shiftjis')),
        ],
        name02: [
          this.Utils.validate.required(this.validateFirstNameLabel()),
          this.Utils.validate.shiftjis(this.$t('common.validate.shiftjis')),
        ],
        kana01: [
          this.Utils.validate.required(this.validateLastNameKanaLabel()),
          this.Utils.validate.kanaFormat(this.validateLastNameKanaLabel()),
        ],
        kana02: [
          this.Utils.validate.required(this.validateFirstNameKanaLabel()),
          this.Utils.validate.kanaFormat(this.validateFirstNameKanaLabel()),
        ],

        birthday_year: [
          this.Utils.validate.numberFormat(this.$t('common.validate.field.birthdayYear')),
          this.Utils.validate.requiredSelect(this.$t('common.validate.field.birthdayYear')),
        ],
        birthday_month: [
          this.Utils.validate.numberFormat(this.$t('common.validate.field.birthdayMonth')),
          this.Utils.validate.requiredSelect(this.$t('common.validate.field.birthdayMonth')),
        ],
        birthday_day: [
          this.Utils.validate.numberFormat(this.$t('common.validate.field.birthdayDay')),
          this.Utils.validate.requiredSelect(this.$t('common.validate.field.birthdayDay')),
        ],

        sex: [this.Utils.validate.requiredSelect(this.$t('common.validate.field.sex'))],

        email: [
          this.Utils.validate.required(this.$t('common.validate.field.email')),
          this.Utils.validate.emailFormat(this.$t('common.validate.field.email')),
        ],
        email_confirm: [
          this.Utils.validate.required(this.$t('common.validate.field.emailConfirm')),
          this.Utils.validate.emailFormat(this.$t('common.validate.field.emailConfirm')),
          { validator: sameEmailValidator, trigger: ['blur'] },
        ],

        postal_code: [
          {
            validator: (rule, value, callback) => postalCodeValidator(rule, value, callback),
            trigger: ['change', 'blur'],
          },
        ],

        pref_id: [this.Utils.validate.requiredSelect(this.$t('common.validate.field.prefecture'))],
        addr01: [this.Utils.validate.required(this.$t('common.validate.field.municipality'))],
        addr02: [this.Utils.validate.required(this.$t('common.validate.field.address'))],

        phone_number: [
          this.Utils.validate.required(this.$t('common.validate.field.phoneNumber')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.phoneNumber')),
          this.Utils.validate.phoneFormat(),
        ],

        company_name: [this.Utils.validate.required(this.$t('common.validate.field.companyName'))],
        company_name_kana: [
          this.Utils.validate.required(this.$t('common.validate.field.companyNameKana')),
          this.Utils.validate.kanaFormat(this.$t('common.validate.field.companyNameKana')),
        ],
      },
    }
  },
  computed: {
    form: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    prefs() {
      return this.$store.state.master.pref
    },
    sex() {
      // 性別選択必須化および「無回答」項目検討中のため、コメントアウト EC-5612
      // const sexUnknow = {
      //   id: this.Constants.SEX_UNKNOW.ID,
      //   name: this.Constants.SEX_UNKNOW.NAME,
      //   sort_no: this.Constants.SEX_UNKNOW.SORT_NO,
      // }
      // return [sexUnknow, ...this.$store.state.master.sex]
      return [...this.$store.state.master.sex]
    },
    YEARS() {
      const currentYear = new Date().getFullYear() - 10
      return Array.from({ length: 91 }, (_, index) => currentYear - index)
    },
    MONTHS() {
      return Array.from(Array(12)).map((_, index) => 1 + index)
    },
    DATES() {
      if (this.form.birthday_year && this.form.birthday_month) {
        const daysInMonth = this.$date.getDaysInMonth(
          this.$date.set(new Date(), { year: this.form.birthday_year, month: this.form.birthday_month - 1 })
        )

        return Array.from(Array(daysInMonth)).map((_, index) => 1 + index)
      } else {
        return []
      }
    },
    nameLabel() {
      return this.corporateFlag
        ? this.$t('common.memberInformation.managerName')
        : this.$t('common.memberInformation.fullName')
    },
    nameKanaLabel() {
      return this.corporateFlag
        ? this.$t('common.memberInformation.managerKana')
        : this.$t('common.memberInformation.nameKana')
    },
  },
  watch: {
    'form.birthday_year'() {
      this.checkDateFeature()
    },
    'form.birthday_month'() {
      if (!this.DATES.includes(this.form.birthday_day)) {
        this.form.birthday_day = 1
      }
      this.checkDateFeature()
    },
    'form.birthday_day'() {
      this.checkDateFeature()
    },
  },
  methods: {
    async getAddress() {
      this.onChangePostalCodeForm()

      this.$refs.form.validateField('postal_code')

      if (!this.form.postal_code01?.trim() || !this.form.postal_code02?.trim()) return

      const res = await PostCodeJpService.getAddress({
        postalCode: this.form.postal_code01 + this.form.postal_code02,
      })

      if (!res) return

      this.form.pref_id = this.prefs.find((item) => item.name === res.prefecture)?.id
      this.form.addr01 = toHalfWidth(res.municipality)
      this.form.addr02 = toHalfWidth(res.addr02)
    },
    onChangePostalCodeForm() {
      this.form = {
        ...this.form,
        postal_code01: changeHalfNumber(this.form.postal_code01),
        postal_code02: changeHalfNumber(this.form.postal_code02),
      }
    },
    onSubmit() {
      if (!this.corporateFlag && this.form.business_account_flag) {
        this.form.name01 = this.form.company_name
        this.form.name02 = ''
        this.form.kana01 = this.form.company_name_kana
        this.form.kana02 = ''
      }
      this.form = trimCommaForm(trimForm(this.form))

      this.$nextTick(() => {
        this.$refs.form.validate(async (isValid) => {
          if (!isValid) {
            return
          }

          this.$emit('submit')
        })
      })
    },
    onCancel() {
      this.$emit('cancel')
    },
    checkDateFeature() {
      const { birthday_year, birthday_month, birthday_day } = this.form

      if (!birthday_year || !birthday_month || !birthday_day) return false

      const now = new Date()
      const birthday = new Date(birthday_year, birthday_month - 1, birthday_day)

      const isAfter = this.$date.isAfter(birthday, now)

      if (isAfter) {
        if (this.$date.getMonth(birthday) > this.$date.getMonth(now)) {
          this.form.birthday_month = 1
        }

        this.form.birthday_day = 1

        this.$alert({
          message: this.$t('common.messages.selectDateFeature'),
          type: 'error',
        })

        return true
      }

      return false
    },
    validateLastNameLabel() {
      return this.corporateFlag
        ? this.$t('common.validate.field.managerLastName')
        : this.$t('common.validate.field.lastName')
    },
    validateFirstNameLabel() {
      return this.corporateFlag
        ? this.$t('common.validate.field.managerFirstName')
        : this.$t('common.validate.field.firstName')
    },
    validateLastNameKanaLabel() {
      return this.corporateFlag
        ? this.$t('common.validate.field.managerLastNameKana')
        : this.$t('common.validate.field.lastNameKana')
    },
    validateFirstNameKanaLabel() {
      return this.corporateFlag
        ? this.$t('common.validate.field.managerFirstNameKana')
        : this.$t('common.validate.field.firstNameKana')
    },
  },
}
</script>

<style></style>
